import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import adminAuthService from '../../services/adminAuthService';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import SessionTable from './components/SessionTable';
import SecurityMetrics from './components/SecurityMetrics';
import ActivityTimeline from './components/ActivityTimeline';

const AdminSessionManagement = () => {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [securityMetrics, setSecurityMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Redirect non-admin users
  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      navigate('/user-wallet-dashboard');
    }
  }, [isAdmin, isAuthenticated, navigate]);

  // Load session data
  const loadSessionData = async () => {
    try {
      const [sessionsResult, logsResult, metricsResult] = await Promise.all([
        adminAuthService?.getActiveSessions(),
        adminAuthService?.getActivityLogs(50),
        adminAuthService?.getSecurityMetrics()
      ]);

      if (sessionsResult?.data) setSessions(sessionsResult?.data);
      if (logsResult?.data) setActivityLogs(logsResult?.data);
      if (metricsResult?.data) setSecurityMetrics(metricsResult?.data);
    } catch (error) {
      console.error('Failed to load session data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadSessionData();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        setRefreshing(true);
        loadSessionData();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadSessionData();
  };

  const handleTerminateSession = async (sessionId) => {
    if (!confirm('Are you sure you want to terminate this session?')) return;

    const { error } = await adminAuthService?.terminateSession(sessionId);
    
    if (error) {
      alert('Failed to terminate session: ' + error?.message);
    } else {
      // Refresh data
      loadSessionData();
    }
  };

  const handleEmergencyLockdown = async () => {
    if (!confirm('⚠️ EMERGENCY LOCKDOWN\n\nThis will terminate ALL active admin sessions including yours. Continue?')) return;

    // Implement emergency lockdown logic
    alert('Emergency lockdown initiated. All sessions will be terminated.');
    await adminAuthService?.adminSignOut(user?.id);
    navigate('/admin-authentication-portal');
  };

  // Access denied for non-admin
  if (!isAdmin) {
    return (
      <MainLayout showControls={false}>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="ShieldAlert" className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-6">
              This area is restricted to authorized administrators only.
            </p>
            <button
              onClick={() => navigate('/user-wallet-dashboard')}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading session data...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Session Management</h1>
              <p className="text-gray-600">Monitor and control active administrative sessions</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <Icon name="RefreshCw" className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
              <button
                onClick={handleEmergencyLockdown}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Icon name="AlertTriangle" className="w-4 h-4" />
                <span className="text-sm font-medium">Emergency Lockdown</span>
              </button>
            </div>
          </div>

          {/* Security Metrics */}
          {securityMetrics && <SecurityMetrics metrics={securityMetrics} />}

          {/* Active Sessions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Active Sessions</h2>
                  <p className="text-sm text-gray-600">{sessions?.length || 0} active admin sessions</p>
                </div>
              </div>
            </div>
            <SessionTable 
              sessions={sessions} 
              onTerminate={handleTerminateSession}
              currentUserId={user?.id}
            />
          </div>

          {/* Activity Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon name="Activity" className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Activity Timeline</h2>
                <p className="text-sm text-gray-600">Recent administrative actions</p>
              </div>
            </div>
            <ActivityTimeline activities={activityLogs} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminSessionManagement;